import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { useFilterPresets } from '../../../shared/filter-presets/use-filter-presets.js';
import {
  ADMIN_NOTIFICATION_TYPES,
  useAdminNotificationRefresh,
} from '../../../shared/realtime/admin-notification-events.js';
import {
  ADMIN_REALTIME_TYPES,
  useAdminRealtimeEvent,
} from '../../../shared/realtime/admin-realtime-events.js';
import {
  ACCOUNT_FILTER_FIELDS,
  ACCOUNT_SORT_FIELDS,
  DEFAULT_ACCOUNT_SORT,
  buildAccountsRequestDto,
  buildSyncSuccessMessage,
  mapAccountsResponseToDto,
} from '../dto/accounts-list.dto.js';
import { buildAccountsTableModel } from '../model/accounts-list.model.js';

const ACCOUNT_PAGE_SIZE = 20;
const ACCOUNT_FILTER_STORAGE_KEY = 'admin_accounts_list_filters';
const EMPTY_ACCOUNT_FILTERS = Object.freeze({
  login: '',
  type: '',
  platform: '',
  currency: '',
  server: '',
});

const DEFAULT_SORT_ORDER_BY_FIELD = Object.freeze({
  created: 'desc',
  modified: 'desc',
  login: 'asc',
  leverage: 'asc',
});

function getEmptyFilters() {
  return { ...EMPTY_ACCOUNT_FILTERS };
}

function normalizeAccountFilters(filters) {
  const nextFilters = getEmptyFilters();

  ACCOUNT_FILTER_FIELDS.forEach((field) => {
    nextFilters[field] = String(filters?.[field] || '').trim();
  });

  return nextFilters;
}

function readSearchParams() {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }

  return new URLSearchParams(window.location.search);
}

function getInitialFiltersFromUrl() {
  const params = readSearchParams();
  const filters = getEmptyFilters();

  ACCOUNT_FILTER_FIELDS.forEach((field) => {
    filters[field] = String(params.get(field) || '').trim();
  });

  return normalizeAccountFilters(filters);
}

function readStoredFilters() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(ACCOUNT_FILTER_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function hasActiveFilters(filters) {
  return ACCOUNT_FILTER_FIELDS.some((field) => String(filters?.[field] || '').trim());
}

function getInitialAccountFilters() {
  const urlFilters = getInitialFiltersFromUrl();
  if (hasActiveFilters(urlFilters)) {
    return urlFilters;
  }

  return normalizeAccountFilters(readStoredFilters() || EMPTY_ACCOUNT_FILTERS);
}

function buildFilterSearchParams(filters) {
  const normalized = normalizeAccountFilters(filters);
  const params = new URLSearchParams();

  ACCOUNT_FILTER_FIELDS.forEach((field) => {
    if (normalized[field]) {
      params.set(field, normalized[field]);
    }
  });

  return params;
}

function persistAccountFilters(filters) {
  if (typeof window === 'undefined') {
    return;
  }

  const normalized = normalizeAccountFilters(filters);
  const active = hasActiveFilters(normalized);

  try {
    if (active) {
      window.sessionStorage.setItem(ACCOUNT_FILTER_STORAGE_KEY, JSON.stringify(normalized));
    } else {
      window.sessionStorage.removeItem(ACCOUNT_FILTER_STORAGE_KEY);
    }
  } catch {
    // ignore unavailable storage
  }

  const params = buildFilterSearchParams(normalized);
  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
  window.history.replaceState(window.history.state, '', nextUrl);
}

function normalizeRealtimeAccount(event) {
  const realtimeEvent = {
    ...event,
    lastSyncFreshnessAt: new Date().toISOString(),
  };

  return mapAccountsResponseToDto([realtimeEvent])[0] || null;
}

function mergeRealtimeAccountRow(row, event) {
  const accountId = String(event?.accountId || event?.uid || '').trim();
  if (!accountId || row.accountId !== accountId) {
    return row;
  }

  const nextRow = normalizeRealtimeAccount(event);
  if (!nextRow) {
    return row;
  }

  return {
    ...row,
    ...nextRow,
    key: row.key,
    accountId: row.accountId,
    profileUid: nextRow.profileUid || row.profileUid,
    profileStatus: nextRow.profileStatus || row.profileStatus,
    profileName: nextRow.profileName && nextRow.profileName !== '-' ? nextRow.profileName : row.profileName,
    created: nextRow.created || row.created,
    source: {
      ...(row.source || {}),
      ...event,
    },
  };
}

export function useAccountsList() {
  const { i18n } = useTranslation();
  const locale = i18n?.resolvedLanguage || i18n?.language;
  const { token } = useAuth();
  const initialFilters = useMemo(() => getInitialAccountFilters(), []);
  const [rows, setRows] = useState([]);
  const [hasMoreAccounts, setHasMoreAccounts] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [syncingId, setSyncingId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sort, setSort] = useState(DEFAULT_ACCOUNT_SORT);
  const [filters, setFilters] = useState(initialFilters);
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [isFilterOpen, setIsFilterOpen] = useState(() => hasActiveFilters(initialFilters));

  const applyPresetFilters = useCallback((presetFilters) => {
    const nextFilters = normalizeAccountFilters(presetFilters);
    setDraftFilters(nextFilters);
    setFilters({ ...nextFilters });
    setIsFilterOpen(true);
  }, []);

  const filterPresetState = useFilterPresets({
    storageKey: 'admin_account_filter_presets',
    filters: draftFilters,
    onApply: applyPresetFilters,
  });

  useEffect(() => {
    persistAccountFilters(filters);
  }, [filters]);

  const loadAccounts = useCallback(async ({ offset = 0, append = false } = {}) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.getAccounts(token, buildAccountsRequestDto({
        limit: ACCOUNT_PAGE_SIZE,
        offset,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder,
        filters,
      }));
      const dto = mapAccountsResponseToDto(response);
      const nextRows = dto;
      setRows((previousRows) => (append ? [...previousRows, ...nextRows] : nextRows));
      setHasMoreAccounts(nextRows.length === ACCOUNT_PAGE_SIZE);
    } catch (requestError) {
      if (!append) {
        setRows([]);
        setHasMoreAccounts(false);
      }
      setError(requestError.message || 'Failed to load accounts');
    } finally {
      if (append) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [filters, sort, token]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useAdminNotificationRefresh(ADMIN_NOTIFICATION_TYPES.accountOpened, () => {
    loadAccounts({ offset: 0, append: false });
  });

  useAdminRealtimeEvent(ADMIN_REALTIME_TYPES.accountSynced, (event) => {
    setRows((currentRows) => currentRows.map((row) => mergeRealtimeAccountRow(row, event)));
  });

  const loadMoreAccounts = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMoreAccounts) {
      return;
    }

    loadAccounts({ offset: rows.length, append: true });
  }, [hasMoreAccounts, isLoading, isLoadingMore, loadAccounts, rows.length]);

  const changeSort = useCallback((field) => {
    const normalizedField = String(field || '').trim();
    if (!ACCOUNT_SORT_FIELDS.includes(normalizedField)) {
      return;
    }

    setSort((currentSort) => {
      if (currentSort.sortBy === normalizedField) {
        return {
          sortBy: normalizedField,
          sortOrder: currentSort.sortOrder === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        sortBy: normalizedField,
        sortOrder: DEFAULT_SORT_ORDER_BY_FIELD[normalizedField] || 'asc',
      };
    });
  }, []);

  const updateFilter = useCallback((field, value) => {
    const normalizedField = String(field || '').trim();
    if (!ACCOUNT_FILTER_FIELDS.includes(normalizedField)) {
      return;
    }

    setDraftFilters((currentFilters) => ({
      ...currentFilters,
      [normalizedField]: String(value || ''),
    }));
  }, []);

  const applyFilters = useCallback(() => {
    setFilters({ ...draftFilters });
  }, [draftFilters]);

  const clearFilters = useCallback(() => {
    const emptyFilters = getEmptyFilters();
    setDraftFilters(emptyFilters);
    setFilters(getEmptyFilters());
  }, []);

  const syncAccount = useCallback(
    async (account) => {
      const accountId = String(account?.accountId || '').trim();
      if (account?.isPendingVerification) {
        return;
      }

      if (!accountId) {
        setSuccess('');
        setError('Failed to sync account: missing account id');
        return;
      }

      setSyncingId(accountId);
      setError('');
      setSuccess('');

      try {
        const response = await adminApi.syncAccount(token, accountId);
        setSuccess(buildSyncSuccessMessage(response));
      } catch (requestError) {
        setError(requestError.message || 'Failed to sync account');
      } finally {
        setSyncingId('');
      }
    },
    [token],
  );

  const displayRows = useMemo(() => buildAccountsTableModel(rows, locale), [rows, locale]);

  return {
    rows: displayRows,
    isLoading,
    isLoadingMore,
    hasMoreAccounts,
    syncingId,
    error,
    success,
    sort,
    filters: draftFilters,
    isFilterOpen,
    changeSort,
    updateFilter,
    applyFilters,
    clearFilters,
    setIsFilterOpen,
    syncAccount,
    loadMoreAccounts,
    ...filterPresetState,
  };
}
